import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import apiErrorMessage from '../utils/apiErrorMessage';
import { POOL_SHARE_FIELDS, POOL_MEMBER_FIELDS, restorablePoolShare, restorableMemberFields, poolShareConfirmed } from '../utils/poolShareRollback';

const _ = require('lodash');

const parseFloatFromString = (value) => {
  if (typeof value === 'number') return value;
  if (value === '' || value === null || value === undefined) return 0.0;
  return parseFloat(String(value).replace(/,/g, ''));
};

// Shared toast options for autosave-error notifications. Top-center, no
// progress bar, non-draggable — matches the existing patterns in this hook
// and V3FinancePage.
const ERROR_TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
};

export const buildV3OfferPayload = (offerData, budgetData, extraFields = {}) => {
  const pick = (field) => {
    const offerVal = offerData?.[field];
    if (offerVal !== undefined && offerVal !== null) return offerVal;
    return budgetData?.[field];
  };

  const parseNumOrEmpty = (val) => (val ? parseFloatFromString(val) : '');
  const parseNumOrZero = (val) => (val ? parseFloatFromString(val) : 0);
  const parseIntOrZero = (val) => (val ? parseInt(val, 10) : 0);
  const parseNumOrNull = (val) =>
    val === '' || val == null ? null : parseFloatFromString(val);

  const softMerch = pick('soft_merch_rate');
  const hardMerch = pick('hard_merch_rate');

  return {
    template_id: offerData.template_id || null,
    dollar_amount: parseNumOrEmpty(pick('dollar_amount')),
    upside_percentage: parseNumOrEmpty(pick('upside_percentage')),
    dollar_or_percentage_operator: pick('dollar_or_percentage_operator'),
    from_dollar_one: pick('from_dollar_one'),
    promoter_profit: pick('promoter_profit'),
    promoter_profit_percentage: parseNumOrZero(pick('promoter_profit_percentage')),
    has_tax_withholding: pick('has_tax_withholding') || false,
    tax_withholding_minimum_threshold: parseNumOrZero(pick('tax_withholding_minimum_threshold')),
    tax_withholding_percentage: parseNumOrZero(pick('tax_withholding_percentage')),
    tax_withholding_due_days: parseIntOrZero(pick('tax_withholding_due_days')),
    has_merchandising: !!(softMerch || hardMerch),
    soft_merch_rate: parseNumOrEmpty(softMerch),
    soft_merch_sold_by: pick('soft_merch_sold_by'),
    soft_merch_notes: pick('soft_merch_notes'),
    hard_merch_rate: parseNumOrEmpty(hardMerch),
    hard_merch_sold_by: pick('hard_merch_sold_by'),
    hard_merch_notes: pick('hard_merch_notes'),
    addl_merch_notes: pick('addl_merch_notes'),
    guarantee_notes: pick('guarantee_notes'),
    revenue_base: pick('revenue_base') || 'default',
    artist_payout_cap: parseNumOrNull(pick('artist_payout_cap')),
    artist_payout_floor: parseNumOrNull(pick('artist_payout_floor')),
    breakeven_threshold: parseNumOrNull(pick('breakeven_threshold')),
    has_facility_maintenance_fee: !!pick('has_facility_maintenance_fee'),
    facility_maintenance_fee: parseNumOrNull(pick('facility_maintenance_fee')),
    has_charity_deduction: !!pick('has_charity_deduction'),
    charity_deduction: parseNumOrNull(pick('charity_deduction')),
    apply_tax_after_facility_fee: !!pick('apply_tax_after_facility_fee'),
    // O-2783 "pool over offers": the per-member share fields. Only included
    // when the offer is actually in a pool (membership keyed by
    // v3_offer_pool_id), so a non-pool offer's PATCH never sends them and
    // Independent-mode offers stay byte-for-byte unchanged.
    ...(offerData?.v3_offer_pool_id
      ? {
        pool_payout_strategy: pick('pool_payout_strategy') || null,
        pool_percentage: parseNumOrNull(pick('pool_percentage')),
      }
      : {}),
    ...extraFields,
  };
};

// Shape the deposit body for the new V3BudgetDeposits endpoint. Only the
// keys the controller permits go through; extras (e.g. `_destroy`, optimistic
// client ids) are stripped here.
const buildDepositPayload = (deposit, budgetId) => ({
  v3_budget_id: budgetId,
  deposit_type: deposit.deposit_type,
  value: deposit.value,
  due_date_type: deposit.due_date_type,
  relative_due_date: deposit.relative_due_date,
  specific_due_date: deposit.specific_due_date,
  paid: deposit.paid,
});

// Top-level offer deal fields echoed from the save response back into context
// after a PATCH. The rail card already refreshes via the returned `lineup`, but
// the Financial Summary reads these fields off the CONTEXT offer — which the
// autosave previously never updated, so a freshly-typed guarantee/% showed $0
// in the summary until a page reload. Echoing the server-confirmed values keeps
// the summary in step with the saved deal.
const ECHOED_OFFER_FIELDS = [
  'dollar_amount', 'upside_percentage', 'dollar_or_percentage_operator', 'from_dollar_one',
  'revenue_base', 'breakeven_threshold', 'artist_payout_cap', 'artist_payout_floor',
  'has_tax_withholding', 'tax_withholding_percentage', 'tax_withholding_minimum_threshold',
  'promoter_profit', 'promoter_profit_percentage',
  'has_facility_maintenance_fee', 'facility_maintenance_fee',
  'has_charity_deduction', 'charity_deduction',
  'apply_tax_after_facility_fee',
  // Pool-share fields are dispatched optimistically to drive the live split
  // preview; echoing the server-confirmed values keeps the preview in step.
  ...POOL_SHARE_FIELDS,
];

export const pickEchoedDealFields = (data) => {
  const out = {};
  ECHOED_OFFER_FIELDS.forEach((f) => {
    if (data && f in data) out[f] = data[f];
  });
  return out;
};

// Hook that owns local-edit state for a single V3Offer/V3Budget pair and
// debounce-PATCHes changes back to the server. Designed to be shared between
// OfferTabsSection and the forthcoming V3OfferDealTermsPane.
//
//   const {
//     localOffer, localBudget,
//     setLocalOffer, setLocalBudget,
//     updateLocalOffer, updateLocalBudget,
//     debouncedAutoSave,
//     isSaving,
//   } = useV3OfferAutosave({ team, csrfToken, getExtraPayload });
//
// - `team` + `csrfToken` come from V3OfferContext at the call site.
// - `getExtraPayload` is an optional () => object that returns additional
//   v3_offer fields to splice into each PATCH (e.g. `additional_terms` HTML
//   from a RichEditor). Read at fire time so latest values win.
// - `debouncedAutoSave(offerData, budgetData)` is the imperative escape hatch
//   for callers that already constructed a "next" snapshot inline.
export default function useV3OfferAutosave({
  team,
  csrfToken,
  getExtraPayload,
}) {
  const { dispatch } = useV3OfferContext();

  const [localOffer, setLocalOffer] = useState(null);
  const [localBudget, setLocalBudget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Keep callbacks current without re-creating the debounced fn on every render.
  const getExtraPayloadRef = useRef(getExtraPayload);
  useEffect(() => {
    getExtraPayloadRef.current = getExtraPayload;
  }, [getExtraPayload]);

  // Set when a field that mirrors into the V3 budget (e.g. Facility fee) is
  // touched, so the next successful save also pulls the updated budget + line
  // items down. The backend after_save callback rewrites the corresponding
  // BEFORE_ADJUSTED_DEDUCTIONS line item, and without this the UI would
  // continue to show the stale value until reload.
  const pendingBudgetRefreshRef = useRef(false);
  // Pre-edit snapshot of the optimistically-dispatched pool-share fields,
  // captured once per save window so a rejected PATCH can roll the context
  // offer back instead of leaving the live preview computing a split the server
  // refused. Applied in the autosave catch; cleared on a successful save.
  const poolShareRollbackRef = useRef(null);
  const BUDGET_MIRRORED_FIELDS = new Set([
    'has_facility_maintenance_fee',
    'facility_maintenance_fee',
    'has_charity_deduction',
    'charity_deduction',
  ]);

  const autoSaveOfferAndBudget = useCallback(
    async (offerData, budgetData) => {
      if (!offerData?.id) return;

      setIsSaving(true);
      const saveStartedAt = Date.now();
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      try {
        const extraFields = getExtraPayloadRef.current
          ? getExtraPayloadRef.current() || {}
          : {};
        const { data } = await axios.patch(
          `/teams/${team.id}/offers/v3/${offerData.id}`,
          { v3_offer: buildV3OfferPayload(offerData, budgetData, extraFields) }
        );

        if (data) {
          dispatch({
            type: V3_ACTIONS.UPDATE_OFFER,
            payload: {
              origin_modified: data.origin_modified,
              template: data.template,
              // Keep the lineup array fresh so the left-hand rail's cards
              // reflect edited Guarantee / state / etc. without a reload.
              lineup: data.lineup,
              // Echo the saved top-level deal fields so the Financial Summary
              // (which reads the context offer) reflects the saved guarantee/%.
              ...pickEchoedDealFields(data),
            },
          });
        }
        // Drop the rollback only when the shadow agrees with what the server
        // just confirmed. A pool-share keystroke that landed while THIS request
        // was in flight is not covered by this response, and dropping the
        // snapshot would leave its own rejection with nothing to restore.
        if (poolShareConfirmed({ current: localOfferRef.current, confirmed: data })) {
          poolShareRollbackRef.current = null;
        }

        if (pendingBudgetRefreshRef.current) {
          pendingBudgetRefreshRef.current = false;
          try {
            const { data: detail } = await axios.get(
              `/teams/${team.id}/offers/v3/${offerData.id}.json`
            );
            const newBudget = detail?.v3_budget;
            if (newBudget) {
              const budgetFields = { ...newBudget };
              delete budgetFields.v3_budget_line_items;
              delete budgetFields.v3_deal_brackets;
              dispatch({ type: V3_ACTIONS.SET_BUDGET, payload: budgetFields });
              dispatch({
                type: V3_ACTIONS.SET_LINE_ITEMS,
                payload: newBudget.v3_budget_line_items || [],
              });
            }
          } catch (e) {
            // Non-fatal: the offer save still succeeded; the budget refresh
            // can be picked up on the next page load. Surface this to the
            // user so they know the UI may be stale.
            // eslint-disable-next-line no-console
            console.error('Budget refresh after autosave failed:', e);
            toast.error(
              'Could not refresh budget — please reload to see latest changes.',
              ERROR_TOAST_OPTS
            );
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Auto-save failed:', error);
        // Roll the optimistic pool-share dispatch back to its pre-edit values
        // so the live split preview doesn't keep showing a split the server
        // rejected (e.g. a duplicate "remainder") until a reload/artist switch.
        const rollback = poolShareRollbackRef.current;
        poolShareRollbackRef.current = null;
        if (rollback && rollback.offerId === (localOfferRef.current?.id ?? null)) {
          // The comparison is against `offerData` — the payload the server
          // actually rejected — because a booker who has since retyped has
          // expressed newer intent than this stale rejection, and their shadow
          // will differ from what was sent. (Comparing against a running record
          // of keystrokes would compare the shadow with itself, so the rollback
          // would never fire at all.) See restorablePoolShare for why a retyped
          // column stops the OTHER one being restored too.
          const args = {
            previous: rollback.previous,
            sent: offerData,
            current: localOfferRef.current,
          };
          // The pair moves all-or-nothing; the clamps and the member deal are
          // independent, so they restore per field.
          const restore = {
            ...(restorableMemberFields(args) || {}),
            ...(restorablePoolShare(args) || {}),
          };

          if (Object.keys(restore).length > 0) {
            dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: restore });
            // Repair the local shadow too, not just the context. The shadow is
            // what every SUBSEQUENT save is built from, so leaving the refused
            // value in it re-sends that value forever — the offer silently stops
            // persisting anything, and the toast blames the pool while the user
            // is editing something else entirely.
            const repaired = { ...localOfferRef.current, ...restore };
            localOfferRef.current = repaired;
            setLocalOffer(repaired);
          }
        }
        // Show WHY the server refused. The controllers render the record's
        // full_messages on a 422, so a rejected pool ("cannot mix different
        // remainder strategies in one pool") can teach the booker what to change
        // instead of leaving them retrying a save that will never succeed.
        toast.error(
          apiErrorMessage(error, 'Failed to save changes. Please try again.'),
          ERROR_TOAST_OPTS
        );
      } finally {
        const elapsed = Date.now() - saveStartedAt;
        const remaining = Math.max(0, 800 - elapsed);
        setTimeout(() => setIsSaving(false), remaining);
      }
    },
    [team, csrfToken, dispatch]
  );

  const debouncedAutoSave = useMemo(
    () =>
      _.debounce((offerData, budgetData) => {
        if (!offerData?.id) return;
        // apply_tax_after_facility_fee is the one field in this payload that
        // another surface owns: marking a budget row writes it server-side
        // (O-4778). The arguments here were frozen when the keystroke fired, so
        // typing a guarantee and then clicking the % marker inside this 600ms
        // would PATCH the pre-click value back over it. Re-read just that field.
        const latest = localOfferRef.current;
        const payload = latest?.id === offerData.id
          ? { ...offerData, apply_tax_after_facility_fee: latest.apply_tax_after_facility_fee }
          : offerData;
        autoSaveOfferAndBudget(payload, budgetData);
      }, 600),
    [autoSaveOfferAndBudget]
  );

  // A cancelled debounce has no save coming, so the snapshot it belongs to has
  // nothing left to answer it — drop it with the request. See the note at the
  // snapshot itself for what a stranded one does.
  const cancelPendingSave = useCallback(() => {
    debouncedAutoSave.cancel();
    poolShareRollbackRef.current = null;
  }, [debouncedAutoSave]);

  // Cancel pending saves when the hook unmounts or the debouncer is replaced.
  useEffect(() => () => cancelPendingSave(), [cancelPendingSave]);

  const updateLocalOffer = useCallback(
    (field, value) => {
      if (BUDGET_MIRRORED_FIELDS.has(field)) {
        pendingBudgetRefreshRef.current = true;
      }
      // Read latest localOffer via ref so this callback doesn't need to be
      // re-created on every offer edit (matches the deposit-side pattern).
      const nextOffer = { ...localOfferRef.current, [field]: value };
      // Advance the ref HERE, not just in the effect that mirrors localOffer.
      // That effect runs after render, so two calls in the same tick would both
      // merge against the pre-edit snapshot and the second would drop the
      // first's field — and the trailing-edge debounce keeps only the second.
      // Selecting a pool strategy does exactly that (strategy + seeded share),
      // and losing the strategy leaves an `explicit` member holding the share of
      // the WHOLE pool. Matches the ref pattern in standards/frontend-react.md.
      localOfferRef.current = nextOffer;
      setLocalOffer(nextOffer);
      debouncedAutoSave(nextOffer, localBudget);
      return nextOffer;
    },
    [localBudget, debouncedAutoSave]
  );

  // The active member's pool-share fields persist through the offer autosave AND
  // dispatch an optimistic UPDATE_OFFER so the live split preview recomputes
  // immediately. Snapshot the pre-edit values once per save window so a rejected
  // PATCH rolls the optimistic dispatch back (see the autosave catch); a
  // successful save clears the snapshot after the echo applies server truth.
  const updatePoolShareField = useCallback(
    (field, value) => {
      const prev = localOfferRef.current || {};
      // Start a fresh snapshot whenever this is a DIFFERENT offer from the one
      // the current snapshot belongs to. A plain "is there one?" guard left a
      // stranded snapshot from a previous artist in place, so the new artist
      // never got one of their own — their rejection was then skipped on the id
      // mismatch and their shadow kept the refused value forever.
      // A snapshot is only meaningful for a save that is still coming. The pane
      // cancels the debounce whenever it reseeds (an artist switch, a pool-mode
      // flip, any external SET_OFFER), which leaves a snapshot behind with no
      // request to answer it — and the id guard alone will not clear it when the
      // reseed is for the SAME offer. A later, unrelated rejection would then
      // restore values from that abandoned window over accepted server state.
      const snapshot = poolShareRollbackRef.current;
      if (!snapshot || snapshot.offerId !== (prev.id ?? null)) {
        poolShareRollbackRef.current = {
          // WHICH offer this snapshot belongs to. Without it, a rejection that
          // lands after the booker has clicked to another artist merges THIS
          // artist's strategy and share into that one's shadow — and since the
          // shadow is the base for every later PATCH, the next keystroke
          // persists it against the wrong artist.
          offerId: prev.id ?? null,
          previous: Object.fromEntries(POOL_MEMBER_FIELDS.map((f) => [f, prev[f] ?? null])),
        };
      }
      dispatch({ type: V3_ACTIONS.UPDATE_OFFER, payload: { [field]: value } });
      updateLocalOffer(field, value);
    },
    [dispatch, updateLocalOffer]
  );

  const updateLocalBudget = useCallback(
    (field, value) => {
      const nextBudget = { ...localBudget, [field]: value };
      setLocalBudget(nextBudget);
      debouncedAutoSave(localOffer, nextBudget);
      return nextBudget;
    },
    [localOffer, localBudget, debouncedAutoSave]
  );

  // ── Deposit autosave ──
  //
  // Deposits no longer ride on the offer PATCH (legacy nested-attributes path
  // removed). Each create/update/delete fires a dedicated request against
  // V3BudgetDepositsController:
  //
  //   POST   /teams/:team_id/budget_deposits/v3
  //   PATCH  /teams/:team_id/budget_deposits/v3/:id
  //   DELETE /teams/:team_id/budget_deposits/v3/:id
  //
  // We debounce per deposit id so rapid edits to the same row coalesce, and
  // we preserve the patches-since-last-fire so no field is dropped.
  const depositChangesByKeyRef = useRef({});
  const depositDebouncersByKeyRef = useRef({});
  // Mirror of localBudget kept in a ref so the debounced fire-time callback can
  // read the latest value without dragging a stale closure capture in.
  const localBudgetRef = useRef(null);
  useEffect(() => {
    localBudgetRef.current = localBudget;
  }, [localBudget]);
  // Mirror of localOffer in a ref so updateLocalOffer can read the latest
  // snapshot without re-creating the callback on every offer edit.
  const localOfferRef = useRef(null);
  useEffect(() => {
    localOfferRef.current = localOffer;
  }, [localOffer]);

  useEffect(() => () => {
    Object.values(depositDebouncersByKeyRef.current).forEach((d) => d.cancel && d.cancel());
  }, []);

  const sendDepositPatch = useCallback(
    async (deposit, snapshotBudget) => {
      if (!deposit?.id || !team?.id) return;
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      setIsSaving(true);
      const saveStartedAt = Date.now();
      try {
        const budgetId = snapshotBudget?.id || deposit.v3_budget_id;
        const { data } = await axios.patch(
          `/teams/${team.id}/budget_deposits/v3/${deposit.id}`,
          { v3_budget_deposit: buildDepositPayload(deposit, budgetId) }
        );

        // Merge the server response back into localBudget so any
        // server-coerced values (e.g. numeric formatting) win.
        setLocalBudget((prev) => {
          if (!prev) return prev;
          const next = (prev.v3_budget_deposits || []).map((d) => (
            d.id === deposit.id ? { ...d, ...(data || {}) } : d
          ));
          const updated = { ...prev, v3_budget_deposits: next };
          dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { v3_budget_deposits: next } });
          return updated;
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Deposit autosave failed:', error);
        // Roll back to the snapshot the user started typing from.
        if (snapshotBudget) {
          setLocalBudget(snapshotBudget);
          dispatch({
            type: V3_ACTIONS.UPDATE_BUDGET,
            payload: { v3_budget_deposits: snapshotBudget.v3_budget_deposits || [] },
          });
        }
        toast.error('Failed to save deposit. Please try again.', ERROR_TOAST_OPTS);
      } finally {
        const elapsed = Date.now() - saveStartedAt;
        const remaining = Math.max(0, 800 - elapsed);
        setTimeout(() => setIsSaving(false), remaining);
      }
    },
    [team, csrfToken, dispatch]
  );

  const createDepositNow = useCallback(
    async (deposit, snapshotBudget) => {
      if (!team?.id) return null;
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const budgetId = snapshotBudget?.id || deposit.v3_budget_id;
      if (!budgetId) return null;

      setIsSaving(true);
      const saveStartedAt = Date.now();
      try {
        const { data } = await axios.post(
          `/teams/${team.id}/budget_deposits/v3`,
          { v3_budget_deposit: buildDepositPayload(deposit, budgetId) }
        );
        // Replace the seed (no-id) deposit with the server-returned record.
        setLocalBudget((prev) => {
          if (!prev) return prev;
          const next = (prev.v3_budget_deposits || []).map((d) => (
            d.id ? d : { ...d, ...data }
          ));
          // If there was no seed in localBudget yet, append the new record.
          const hasMatch = next.some((d) => d.id === data?.id);
          const final = hasMatch ? next : [...next, data];
          dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { v3_budget_deposits: final } });
          return { ...prev, v3_budget_deposits: final };
        });
        return data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Deposit create failed:', error);
        toast.error('Failed to add deposit. Please try again.', ERROR_TOAST_OPTS);
        return null;
      } finally {
        const elapsed = Date.now() - saveStartedAt;
        const remaining = Math.max(0, 800 - elapsed);
        setTimeout(() => setIsSaving(false), remaining);
      }
    },
    [team, csrfToken, dispatch]
  );

  const deleteDepositNow = useCallback(
    async (deposit, snapshotBudget) => {
      if (!deposit?.id || !team?.id) return;
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      setIsSaving(true);
      const saveStartedAt = Date.now();
      try {
        await axios.delete(`/teams/${team.id}/budget_deposits/v3/${deposit.id}`);
        setLocalBudget((prev) => {
          if (!prev) return prev;
          const next = (prev.v3_budget_deposits || []).filter((d) => d.id !== deposit.id);
          dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: { v3_budget_deposits: next } });
          return { ...prev, v3_budget_deposits: next };
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Deposit delete failed:', error);
        // Roll back to the snapshot.
        if (snapshotBudget) {
          setLocalBudget(snapshotBudget);
          dispatch({
            type: V3_ACTIONS.UPDATE_BUDGET,
            payload: { v3_budget_deposits: snapshotBudget.v3_budget_deposits || [] },
          });
        }
        toast.error('Failed to remove deposit. Please try again.', ERROR_TOAST_OPTS);
      } finally {
        const elapsed = Date.now() - saveStartedAt;
        const remaining = Math.max(0, 800 - elapsed);
        setTimeout(() => setIsSaving(false), remaining);
      }
    },
    [team, csrfToken, dispatch]
  );

  // Public deposit API used by V3OfferDealTermsPane / dealTermsConfig.
  //
  // `patch` is the field deltas being applied to the FIRST deposit. The hook:
  //   * `_destroy: true` on an id-bearing deposit  → DELETE immediately.
  //   * `_destroy: true` on a no-id seed           → drop locally.
  //   * id-less seed gets a real value             → POST immediately
  //     (creates the deposit on the server; future edits debounce-PATCH).
  //   * id-bearing deposit edit                    → debounced PATCH per id.
  //
  // Single-deposit assumption: the deal-terms pane today renders ONE deposit
  // editor (see dealTermsConfig.jsx). updateLocalDeposit therefore operates on
  // current[0] only — edits to a hypothetical second deposit would silently
  // overwrite the first. If the UI ever shows multiple deposit editors, key
  // this on a stable client id and accept a depositKey parameter instead.
  const updateLocalDeposit = useCallback(
    (patch) => {
      if (!localBudget) return null;
      const current = localBudget.v3_budget_deposits || [];
      // No `team_id` in the seed: V3BudgetDepositsController doesn't permit
      // it (it derives team from `budget.team_id` server-side).
      const first = current[0] || {
        v3_budget_id: localBudget.id,
        deposit_type: 'flat',
        due_date_type: 'relative',
      };
      const nextFirst = { ...first, ...patch };
      const nextDeposits = current.length === 0
        ? [nextFirst]
        : [nextFirst, ...current.slice(1)];
      const nextBudget = { ...localBudget, v3_budget_deposits: nextDeposits };
      const snapshot = localBudget;

      setLocalBudget(nextBudget);

      // ── Destroy ──
      if (patch._destroy) {
        if (first.id) {
          deleteDepositNow(first, snapshot);
        } else {
          // No persisted row yet — just drop the seed locally.
          const cleared = { ...localBudget, v3_budget_deposits: current.slice(1) };
          setLocalBudget(cleared);
          dispatch({
            type: V3_ACTIONS.UPDATE_BUDGET,
            payload: { v3_budget_deposits: cleared.v3_budget_deposits },
          });
        }
        return nextBudget;
      }

      // ── Create (seed gets real data) ──
      if (!first.id) {
        // Only POST when there's enough to create a meaningful row. We treat
        // either a non-null value or a non-default deposit_type/due_date_type
        // change as "user has actually started filling this in".
        const hasMeaningfulValue = nextFirst.value != null && nextFirst.value !== '';
        if (hasMeaningfulValue) {
          createDepositNow(nextFirst, snapshot);
        }
        return nextBudget;
      }

      // ── Update (debounce per deposit id) ──
      const key = first.id;
      depositChangesByKeyRef.current[key] = {
        ...(depositChangesByKeyRef.current[key] || {}),
        ...patch,
      };
      if (!depositDebouncersByKeyRef.current[key]) {
        depositDebouncersByKeyRef.current[key] = _.debounce(() => {
          // Read latest localBudget via ref so we never act on a stale closure
          // capture from when this debouncer was first created.
          const latest = localBudgetRef.current;
          if (!latest) return;
          const target = (latest.v3_budget_deposits || []).find((d) => d.id === key);
          if (!target) return;
          const merged = { ...target, ...(depositChangesByKeyRef.current[key] || {}) };
          depositChangesByKeyRef.current[key] = {};
          sendDepositPatch(merged, latest);
        }, 600);
      }
      depositDebouncersByKeyRef.current[key]();

      return nextBudget;
    },
    [localOffer, localBudget, team, dispatch, sendDepositPatch, createDepositNow, deleteDepositNow]
  );

  return {
    localOffer,
    setLocalOffer,
    localBudget,
    setLocalBudget,
    updateLocalOffer,
    updatePoolShareField,
    updateLocalBudget,
    updateLocalDeposit,
    autoSaveOfferAndBudget,
    debouncedAutoSave,
    cancelPendingSave,
    isSaving,
    setIsSaving,
  };
}
